import { StyleSheet, Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import { RiskDot } from "src/core/components/pdf/shared/RiskDot";
import { IRiskOption } from "src/core/constants/options";
import { CustomRiskLevel } from "src/core/constants/risk";
import { ElementInputValue, Mission, MissionStatus } from "src/core/gql/graphql";
import { formatIsoToDate } from "src/core/utils/date";
import { getInputValue } from "src/core/utils/folderAnalysis";

const styles = StyleSheet.create({
  table: {
    marginBottom: 5,
    borderWidth: 1,
    borderColor: "#E6E8ED",
    borderRadius: 12,
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F5F6F9",
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E6E8ED",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    alignItems: "center",
  },
  tableHeaderCell: {
    flex: 1,
    fontSize: 10,
    fontWeight: "bold",
    color: "#0A1A53",
    textTransform: "uppercase",
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E6E8ED",
    alignItems: "center",
  },
  tableCell: {
    flex: 1,
    fontSize: 10,
    color: "#0A1A53",
  },
  missionNameCell: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  riskDot: {
    width: 14,
    height: 14,
    borderRadius: 8,
  },
  missionNameText: {
    fontSize: 10,
    color: "#0A1A53",
  },
  tableCellInactive: {
    flex: 1,
    fontSize: 10,
    color: "#0A1A53",
    opacity: 0.35,
  },
  missionNameTextInactive: {
    fontSize: 10,
    color: "#0A1A53",
    opacity: 0.35,
  },
  closedMissionContainer: {
    width: "100%",
    backgroundColor: "#F5F6FA",
  },
  closedMissionRow: {
    width: "80%",
    alignSelf: "flex-end",
    paddingVertical: 8,
    paddingHorizontal: 8,
    marginBottom: 10,
    marginRight: 10,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#DFE2E9",
    borderLeftWidth: 1,
    borderLeftColor: "#DFE2E9",
    borderRightWidth: 1,
    borderRightColor: "#DFE2E9",
  },
  closedMissionText: {
    fontSize: 9,
    color: "#0A1A53",
    opacity: 0.35,
    marginBottom: 4,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  closedMissionDateText: {
    fontSize: 9,
    color: "#A9AFC3",
  },
});

type MissionsTableProps = {
  missions: Mission[];
  missionTypeWithRiskOptions: IRiskOption[];
  title: string;
  isInactive?: boolean;
  isFirstTable?: boolean;
  hasSecondTable?: boolean;
};

export const MissionsTable = ({
  missions,
  missionTypeWithRiskOptions,
  title,
  isInactive = false,
  isFirstTable = false,
  hasSecondTable = false,
}: MissionsTableProps) => {
  const { t } = useTranslation();

  if (missions.length === 0) return null;

  const tableStyle = [
    styles.table,
    ...(isFirstTable && hasSecondTable
      ? [
          {
            marginBottom: 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomWidth: 0,
          },
        ]
      : []),
    ...(!isFirstTable
      ? [
          {
            marginBottom: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderTopWidth: 0,
          },
        ]
      : []),
  ];

  const headerStyle = [
    styles.tableHeader,
    ...(isFirstTable && hasSecondTable
      ? [{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }]
      : []),
    ...(!isFirstTable ? [{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }] : []),
  ];

  return (
    <View style={tableStyle}>
      <View style={headerStyle}>
        <Text style={styles.tableHeaderCell}>{title}</Text>
        <Text style={styles.tableHeaderCell}>{t("pdf.sections.missions.renewal")}</Text>
      </View>
      {missions.map((mission, index) => {
        const missionTypeCode = getMissionFieldValue(mission, "missionTypeCode");
        const missionName =
          missionTypeWithRiskOptions.find((option) => option.value === missionTypeCode)?.label ??
          missionTypeCode;
        const renewDate = getMissionFieldValue(mission, "renewDate");
        const riskLevel = getMissionRiskLevel(missionTypeCode, missionTypeWithRiskOptions);
        const isLastRow = index === missions.length - 1;
        const isClosed = mission.status === MissionStatus.Closed;
        // Garder la bordure en bas de la dernière ligne si c'est le premier tableau avec un deuxième tableau
        // Ne pas enlever la bordure si c'est une mission fermée (la ligne de clôture aura sa propre bordure)
        const shouldRemoveBottomBorder =
          isLastRow && !(isFirstTable && hasSecondTable) && !isClosed;

        return (
          <View key={mission.id}>
            <View
              style={[
                styles.tableRow,
                ...(shouldRemoveBottomBorder ? [{ borderBottomWidth: 0 }] : []),
                ...(isClosed ? [{ borderBottomWidth: 0 }] : []),
              ]}
            >
              <View style={styles.missionNameCell}>
                <RiskDot riskLevel={riskLevel} />
                <Text style={isInactive ? styles.missionNameTextInactive : styles.missionNameText}>
                  {missionName}
                </Text>
              </View>
              <Text style={isInactive ? styles.tableCellInactive : styles.tableCell}>
                {formatIsoToDate(renewDate)}
              </Text>
            </View>
            {isClosed && (
              <View
                style={[
                  styles.closedMissionContainer,
                  ...(isLastRow && !(isFirstTable && hasSecondTable)
                    ? [
                        {
                          borderBottomLeftRadius: 12,
                          borderBottomRightRadius: 12,
                        },
                      ]
                    : []),
                ]}
              >
                <View style={styles.closedMissionRow}>
                  <Text style={styles.closedMissionText}>
                    {t("pdf.sections.missions.closedMission", {
                      date: formatIsoToDate(mission.adminStatus.lastModification),
                    })}
                  </Text>
                  {mission.statusComment && (
                    <Text style={styles.closedMissionDateText}>{mission.statusComment}</Text>
                  )}
                </View>
              </View>
            )}
          </View>
        );
      })}
    </View>
  );
};

// Fonction pour extraire la valeur d'un champ d'une mission
const getMissionFieldValue = (mission: Mission, fieldCode: string): string => {
  return getInputValue(mission.inputValues as ElementInputValue[], fieldCode) ?? "";
};

// Fonction pour obtenir le niveau de risque d'une mission
const getMissionRiskLevel = (
  missionTypeCode: string,
  missionTypeWithRiskOptions: IRiskOption[]
): CustomRiskLevel => {
  const missionType = missionTypeWithRiskOptions.find((option) => option.value === missionTypeCode);
  return missionType?.risk ?? CustomRiskLevel.UNDEFINED;
};
